import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import {
  defaultAttachmentOderDesOptions,
  defaultSubcontractorStatusOption,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Checkbox, Col, Form, Row, Select } from 'antd';
import React, { Ref, useEffect, useImperativeHandle } from 'react';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type AttachmentOrderFormValues = {
  effectiveDate: string;
  endDate: string;
  type: string;
  deductionThisPeriod: string;
  totalDeduction: string;
  code: string;
  deductionDesc: Option<string> | null;
  status: Option<string> | null;
  priority: string;
  deductionRate: string;
  protectedMin: string;
  protectedMinType: 'percentage' | 'amount';
  marginFee: boolean;
  orderRef: string;
  totalAttachment: string;
};

export type AttachmentOrderFormRef = {
  submit: () => void;
};

type AttachmentOrderFormProps = {
  formRef?: Ref<AttachmentOrderFormRef>;
  onSubmit?: (values: AttachmentOrderFormValues) => void;
  defaultValues?: Partial<AttachmentOrderFormValues>;
  isEdit?: boolean;
};

const AttachmentOrderForm: React.FC<AttachmentOrderFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  isEdit,
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AttachmentOrderFormValues>();
  const protectedMinType = Form.useWatch('protectedMinType', form);
  // Protected minimum is GBP when the addon selector says amount, % otherwise.
  const ProtectedMinInput =
    protectedMinType === 'amount' ? AppCurrencyInput : AppRateInput;

  // Deduction method is not a choice: each deduction description carries its own
  // method, so mirror the selected option's type into the read-only field.
  const deductionDesc = Form.useWatch('deductionDesc', form);

  useEffect(() => {
    form.setFieldValue(
      'type',
      defaultAttachmentOderDesOptions.find(
        (item) => item.value === deductionDesc?.value,
      )?.type ?? 'Table',
    );
  }, [deductionDesc?.value]);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: AttachmentOrderFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {/* Row 1: Deduction Description (full width) */}
      <Form.Item
        label={messages['common.deductionDescription']}
        name="deductionDesc"
      >
        <AppSelect options={defaultAttachmentOderDesOptions} />
      </Form.Item>

      {/* Row 2: Deduction Method (full width, disabled) */}
      <Form.Item label={messages['common.deductionMethod']} name="type">
        <AppInput placeholder={messages['form.enter']} disabled />
      </Form.Item>

      {/* Row 3: Status + Priority */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.status']} name="status">
            <AppSelect options={defaultSubcontractorStatusOption} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.priority']} name="priority">
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 4: Effective Date + End Date */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.effectiveDate']}
            name="effectiveDate"
          >
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.endDate']} name="endDate">
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 5: Protected Minimum + Deduction Rate */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.protectedMinimum']}
            name="protectedMin"
          >
            <ProtectedMinInput
              addonBefore={
                <Form.Item name="protectedMinType" noStyle initialValue="percentage">
                  <Select style={{ width: 60 }}>
                    <Select.Option value="percentage">%</Select.Option>
                    <Select.Option value="amount">£</Select.Option>
                  </Select>
                </Form.Item>
              }
              placeholder={messages['form.enter']}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.deductionOrRate']}
            name="deductionRate"
          >
            <AppCurrencyInput prefix="£" placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 6: Margin Fee + Total Attachment */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item name="marginFee" valuePropName="checked">
            <Checkbox>
              {messages['common.marginFee']} (£1)
            </Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.totalAttachment']}
            name="totalAttachment"
            rules={[
              {
                // Optional (empty = no cap), but a value entered must be above 0.
                validator: async (_, value) => {
                  if (value !== '' && value != null && Number(value) <= 0) {
                    throw new Error(messages['form.mustBeGreaterThanZero']);
                  }
                },
              },
            ]}
          >
            <AppCurrencyInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>

      {/* Row 7: Deduction This Period + Total Deduction (read-only, edit mode only) */}
      {isEdit && (
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.deductionThisPeriod']}
              name="deductionThisPeriod"
            >
              <AppCurrencyInput prefix="£" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.totalDeduction']}
              name="totalDeduction"
            >
              <AppCurrencyInput prefix="£" disabled />
            </Form.Item>
          </Col>
        </Row>
      )}
    </AppFormContainer>
  );
};

export default AttachmentOrderForm;
